import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.DisplayStyle.Companion.Flex
import org.jetbrains.compose.web.css.FlexWrap.Companion.Wrap
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.dom.*

@Composable
fun <Datatype> FilterableList(
    filterSpecs: List<FilterSpec<Datatype>>,
    allData: List<Datatype>,
    itemDisplay: @Composable (Datatype) -> Unit
) {
    var activeData by remember { mutableStateOf(allData) }
    val activeFilters = remember { mutableStateMapOf<FilterSpec<Datatype>, (Datatype) -> Boolean>() }
    Div({ style { display(Flex); flexWrap(Wrap); alignItems("top") } }) {
        for (filterSpec in filterSpecs) {
            filterSpec.render(allData) {
                activeFilters[filterSpec] = it
                var newActiveData = allData
                for (activeFilter in activeFilters.values) {
                    newActiveData = newActiveData.filter(activeFilter)
                }
                activeData = newActiveData
            }
        }
    }
    PagedList(30, activeData, itemDisplay)
}

interface FilterSpec<Datatype> {
    @Composable
    fun render(data: List<Datatype>, onFilterChange: ((Datatype) -> Boolean) -> Unit)
}

class CheckboxFilterSpec<Datatype, TraitType>(
    val title: String,
    val traits: List<TraitType>,
    val filterFunction: ((String, TraitType) -> Boolean)?,
    val renderTrait: @Composable (TraitType) -> Unit,
    val filter: (Datatype, Collection<TraitType>) -> Boolean
) : FilterSpec<Datatype> {
    @Composable
    override fun render(data: List<Datatype>, onFilterChange: ((Datatype) -> Boolean) -> Unit) {
        CheckboxFilterDisplay(title, traits, filterFunction, renderTrait) { selectedTraits ->
            onFilterChange { value: Datatype -> filter(value, selectedTraits) }
        }
    }
}

@Composable
fun <TraitType> CheckboxFilterDisplay(
    title: String,
    traits: List<TraitType>,
    filterFunction: ((String, TraitType) -> Boolean)?,
    renderTrait: @Composable (TraitType) -> Unit,
    onFilterChange: (Set<TraitType>) -> Unit
) {
    val startingState = traits.map { it to true }.toTypedArray()
    val checkboxGroupState = remember {
        mutableStateMapOf(*startingState)
    }
    val hasFilter = checkboxGroupState.any { !it.value }

    FilterBox(title, hasFilter, traits, filterFunction, {
        Button({
            style { myButtonStyle() }; onClick {
            traits.forEach { checkboxGroupState[it] = true }; onFilterChange(
            checkboxGroupState.filterValues { it }.keys
        )
        }
        }) { Text("select all") }
        Button({
            style { myButtonStyle() }; onClick {
            traits.forEach { checkboxGroupState[it] = false }; onFilterChange(
            checkboxGroupState.filterValues { it }.keys
        )
        }
        }) { Text("select none") }
    }) { trait ->
        Div {
            Label {
                CheckboxInput {
                    onChange {
                        checkboxGroupState[trait] = it.value
                        onFilterChange(checkboxGroupState.filterValues { it }.keys)
                    }
                    checked(checkboxGroupState.getOrElse(trait) { false })
                }
                renderTrait(trait)
            }
        }
    }
}

class TextFilterSpec<Datatype>(val title: String, val match: (Datatype, String) -> Boolean) : FilterSpec<Datatype> {
    @Composable
    override fun render(data: List<Datatype>, onFilterChange: ((Datatype) -> Boolean) -> Unit) {
        var search: String by remember { mutableStateOf("") }
        FilterBox<Nothing>(title, search != "", emptyList(), null, {
            TextInput(search) {
                onInput {
                    search = it.value
                }
                onChange { changeEvent ->
                    onFilterChange {
                        match(it, changeEvent.value)
                    }
                }
            }
        }, null)
    }
}